import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  List,
  ListItem,
  Divider,
  Container,
} from '@mui/material';
import api from '../../core/axiosInstance';
import { formatDate } from './formatDate';

const Bill = ({ onClose, id }) => {
  const [billData, setBillData] = useState({
    statement: [{}],
    payment: {},
    company: {},
    profile: {},
    contract: {},
    reciever: {},
  });
  const [total, setTotal] = useState(0);
  const [totalText, setTotalText] = useState('');
  const [addressCurrent, setAddressCurrent] = useState('');

  useEffect(() => {
    const fetchBillData = async () => {
      try {
        const response = await api.get(`/api/payment/bills/${id}`);
        const data = response.data;

        setBillData(data);
        setTotal(data.total);
        setTotalText(data.totalText);

        const addressCurrentMeta = data.profileMetas?.find(
          (m) => m.key === 'addressCurrent'
        );
        if (addressCurrentMeta) {
          const address = extractAddress(addressCurrentMeta.value);
          setAddressCurrent(address);
        }
      } catch (error) {
        console.error('Error fetching bill data:', error);
      }
    };

    if (id) {
      fetchBillData();
    }
  }, [id]);

  const extractAddress = (jsonString) => {
    try {
      const obj = JSON.parse(jsonString);
      const keys = [
        { key: 'houseNo', prefix: '' },
        { key: 'houseMoo', prefix: 'หมู่ ' },
        { key: 'houseAlley', prefix: 'ตรอก/ซอย ' },
        { key: 'village', prefix: '' },
        { key: 'houseRd', prefix: 'ถนน' },
        { key: 'district', prefix: 'ตำบล' },
        { key: 'amphoe', prefix: 'อำเภอ' },
        { key: 'province', prefix: 'จังหวัด' },
        { key: 'zipcode', prefix: '' },
      ];

      return keys
        .map((k) => {
          const item = obj.find((o) => o.key === k.key);
          return item ? `${k.prefix}${item.value}` : '';
        })
        .filter(Boolean)
        .join(' ');
    } catch (error) {
      return '';
    }
  };

  const formatNumber = (number) =>
    number?.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }) || '0';

  const getPaymentMethodText = () => {
    switch (billData.payment.paymentMethod) {
      case 1:
        return 'เงินสด';
      case 2:
        return (
          <>
            โอนผ่านธนาคาร
            <br />( ธนาคาร: {billData.bankName} เลขที่บัญชี:{' '}
            {billData.bankAccount})
          </>
        );
      default:
        return '';
    }
  };

  return (
    <Container maxWidth="sm" sx={{ p: { xs: 1, sm: 2 } }}>
      <Paper elevation={3} sx={{ p: { xs: 1, sm: 2 }, mt: 1 }}>
        {/* Header */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={1}
        >
          <Typography
            variant="h6"
            sx={{ fontSize: { xs: '1rem', sm: '1.5rem' } }}
          >
            {billData.payment.referencesNo}
          </Typography>
        </Box>

        <List sx={{ padding: 0 }}>
          {/* Company and Payment Info */}
          <ListItem sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={6}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                >
                  สาขารับชำระ
                </Typography>
                <Typography sx={{ fontSize: { xs: '0.9rem', sm: '1.25rem' } }}>
                  {billData.company.name}
                </Typography>
                <Typography
                  variant="subtitle2"
                  sx={{ mt: 1, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                >
                  ที่อยู่
                </Typography>
                <Typography sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                  {billData.company.address} ตำบล{billData.company.district} อ.
                  {billData.company.amphoe} จ.{billData.company.province}{' '}
                  {billData.company.zipcode}
                </Typography>
                <Typography
                  variant="subtitle2"
                  sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                >
                  เลขที่นิติบุคคล
                </Typography>
                <Typography sx={{ fontSize: { xs: '0.9rem', sm: '1.25rem' } }}>
                  {billData.company.taxNumber}
                </Typography>
              </Grid>
              <Grid
                item
                xs={12}
                sm={6}
                sx={{ textAlign: { xs: 'left', sm: 'right' } }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                >
                  เลขที่ใบรับเงิน
                </Typography>
                <Typography sx={{ fontSize: { xs: '0.9rem', sm: '1.25rem' } }}>
                  {billData.payment.referencesNo}
                </Typography>
                <Typography
                  variant="subtitle2"
                  sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                >
                  วันที่
                </Typography>
                <Typography sx={{ fontSize: { xs: '0.9rem', sm: '1.25rem' } }}>
                  {formatDate(billData.statement[0]?.paymentDate)}
                </Typography>
                <Typography
                  variant="subtitle2"
                  sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                >
                  สัญญาเลขที่
                </Typography>
                <Typography sx={{ fontSize: { xs: '0.9rem', sm: '1.25rem' } }}>
                  {billData.contract.contractNumber}
                </Typography>
              </Grid>
            </Grid>
          </ListItem>

          {/* Profile Info */}
          <ListItem>
            <Grid container>
              <Grid item xs={12}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                >
                  ผู้ชำระ
                </Typography>
                <Typography sx={{ fontSize: { xs: '0.9rem', sm: '1.25rem' } }}>
                  {billData.profile.prefix}
                  {billData.profile.firstName} {billData.profile.lastName}
                </Typography>
                <Typography
                  variant="subtitle2"
                  sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                >
                  ที่อยู่
                </Typography>
                <Typography sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                  {addressCurrent}
                </Typography>
              </Grid>
            </Grid>
          </ListItem>

          {/* Statement Header */}
          <Divider />
          <ListItem>
            <Grid container>
              <Grid item xs={4}>
                <Typography sx={{ fontSize: { xs: '0.75rem', sm: '1rem' } }}>
                  รายการ
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography
                  align="center"
                  sx={{ fontSize: { xs: '0.75rem', sm: '1rem' } }}
                >
                  งวด
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography
                  align="right"
                  sx={{ fontSize: { xs: '0.75rem', sm: '1rem' } }}
                >
                  รวม
                </Typography>
              </Grid>
            </Grid>
          </ListItem>
          <Divider />

          {/* Statement Items */}
          {billData.statement.map((s, index) => (
            <ListItem key={index}>
              <Grid container>
                <Grid item xs={4}>
                  <Typography
                    sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                  >
                    ค่างวด
                  </Typography>
                  {billData.contract.type === 99 && (
                    <Typography
                      sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                    >
                      ค่าใช้จ่ายการฟ้องและดอกเบี้ยค้าง
                    </Typography>
                  )}
                  <Typography
                    sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                  >
                    เงินต้น
                  </Typography>
                  <Typography
                    color="primary"
                    sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}
                  >
                    {formatNumber(billData.statement[0]?.paidPrincipal)}
                  </Typography>
                  <Typography
                    sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                  >
                    ดอกเบี้ย
                  </Typography>
                  <Typography
                    color="primary"
                    sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}
                  >
                    {formatNumber(billData.statement[0]?.paidInterest)}
                  </Typography>
                  {billData.payment.paymentOthers?.map((other, idx) => (
                    <Box key={idx}>
                      <Typography
                        sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                      >
                        {other.title}
                      </Typography>
                      <Typography
                        color="primary"
                        sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}
                      >
                        {formatNumber(other.amount)}
                      </Typography>
                    </Box>
                  ))}
                </Grid>
                <Grid item xs={4} sx={{ textAlign: 'center' }}>
                  <Typography
                    sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                  >
                    {formatDate(s.startDate)} - {formatDate(s.balanceOnDate)}
                  </Typography>
                </Grid>
                <Grid item xs={4} sx={{ textAlign: 'right' }}>
                  <Typography
                    color="primary"
                    sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}
                  >
                    {formatNumber(total)}
                  </Typography>
                  {billData.contract.type === 99 && (
                    <Typography
                      color="primary"
                      sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}
                    >
                      {formatNumber(s.paidPrincipal2)}
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </ListItem>
          ))}

          {/* Total */}
          <Divider />
          <ListItem>
            <Grid container>
              <Grid item xs={8}>
                <Typography sx={{ fontSize: { xs: '0.75rem', sm: '1rem' } }}>
                  จำนวนเงินรวม (ตัวอักษร){' '}
                  <Box
                    component="span"
                    sx={{
                      fontWeight: 200,
                      fontSize: { xs: '0.75rem', sm: '1rem' },
                    }}
                  >
                    -{totalText}-
                  </Box>
                </Typography>
              </Grid>
              <Grid item xs={4} sx={{ textAlign: 'right' }}>
                <Typography
                  color="primary"
                  sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}
                >
                  {formatNumber(total)}
                </Typography>
              </Grid>
            </Grid>
          </ListItem>
          <Divider />

          {/* Payment Method */}
          <ListItem>
            <Grid container>
              <Grid item xs={12}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                >
                  จ่ายชำระโดย
                </Typography>
                <Typography sx={{ fontSize: { xs: '0.9rem', sm: '1.25rem' } }}>
                  {getPaymentMethodText()}
                </Typography>
              </Grid>
            </Grid>
          </ListItem>

          {/* Balance */}
          <ListItem>
            <Grid container>
              <Grid item xs={12}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                >
                  เงินคงเหลือ
                </Typography>
                <Typography
                  color="primary"
                  sx={{ fontSize: { xs: '0.9rem', sm: '1.25rem' } }}
                >
                  {formatNumber(billData.balance)}
                  {billData.contract.type === 99 &&
                    ` / ${formatNumber(billData.balance2)}`}
                </Typography>
              </Grid>
            </Grid>
          </ListItem>

          {/* Receiver */}
          <ListItem>
            <Grid container>
              <Grid item xs={12}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                >
                  พนักงานรับชำระ
                </Typography>
                <Typography sx={{ fontSize: { xs: '0.9rem', sm: '1.25rem' } }}>
                  {billData.reciever.name} {billData.reciever.lastname}
                </Typography>
              </Grid>
            </Grid>
          </ListItem>

          {/* Note */}
          <ListItem>
            <Grid container>
              <Grid item xs={12}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                >
                  หมายเหตุ
                </Typography>
                <Typography sx={{ fontSize: { xs: '0.9rem', sm: '1.25rem' } }}>
                  {billData.payment.note}
                </Typography>
              </Grid>
            </Grid>
          </ListItem>
        </List>
      </Paper>
    </Container>
  );
};

export default Bill;
